import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ProtectedRouteProps } from '../../types';
import { authLogger } from '../../utils/logger';
import { LoadingPage } from '../ui/LoadingSpinner';
import { isAdminOnlyUser } from '../../types/roles';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireOnboarding = true 
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const [msalInitialized, setMsalInitialized] = useState(false);
  const pendingAdminReturnUrl = typeof window !== 'undefined'
    ? localStorage.getItem('adminReturnUrl')
    : null;

  // Track MSAL initialization status
  useEffect(() => {
    // Check if MSAL is already initialized
    const checkInitialization = (): boolean => {
      const initialized = !!window.__MSAL_INITIALIZED__;
      setMsalInitialized(initialized);
      return initialized;
    };

    // Check immediately
    if (checkInitialization()) {
      return; // Already initialized
    }

    // Listen for the custom event
    const handleMsalInitialized = (): void => {
      authLogger.debug('ProtectedRoute received MSAL initialization event');
      setMsalInitialized(true);
    };

    window.addEventListener('msalInitialized', handleMsalInitialized);

    return () => {
      window.removeEventListener('msalInitialized', handleMsalInitialized);
    };
  }, []);

  // Show loading spinner while checking authentication or MSAL initialization
  if (isLoading || !msalInitialized) {
    authLogger.debug('ProtectedRoute showing loading spinner', { isLoading, msalInitialized });
    return <LoadingPage />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    authLogger.debug('ProtectedRoute redirecting to login - not authenticated');
    return <Navigate to="/login" replace />;
  }

  if (pendingAdminReturnUrl?.startsWith('/admin')) {
    authLogger.debug('ProtectedRoute honoring pending admin return URL');
    return <Navigate to={pendingAdminReturnUrl} replace />;
  }

  // Redirect admin-only users to admin portal (no main app access)
  if (isAdminOnlyUser(user?.roles)) {
    authLogger.debug('ProtectedRoute redirecting admin-only user to admin portal');
    return <Navigate to="/admin" replace />;
  }

  // Redirect to onboarding if required, not completed, AND user has consent configuration
  if (requireOnboarding && user && !user.hasCompletedOnboarding() && user.consent) {
    authLogger.debug('ProtectedRoute redirecting to onboarding - not completed and consent config exists');
    return <Navigate to="/onboarding" replace />;
  }

  authLogger.debug('ProtectedRoute allowing access to protected content');
  return <>{children}</>;
};

export default ProtectedRoute;
